import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTenantConfig } from '../../contexts/TenantConfigContext';

const gradientTextSx = {
  background: 'linear-gradient(135deg, var(--teal), var(--cyan))',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}

const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({ title, lastUpdated, children }) => {
  const navigate = useNavigate();
  const { config } = useTenantConfig();
  const brandName = config?.branding?.displayName || 'RAF';
  const contactEmail = config?.accountManager?.email || 'support@raf.local';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'var(--bg)', color: 'var(--t1)' }}>
      <Box
        component="nav"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          bgcolor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, gap: 2 }}>
            <Button
              startIcon={<ArrowLeft size={18} />}
              onClick={() => navigate('/')}
              sx={{
                color: 'var(--t2)',
                textTransform: 'none',
                '&:hover': { color: 'var(--teal)', bgcolor: 'transparent' },
              }}
            >
              Back
            </Button>
            <Typography
              sx={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 700,
                fontSize: '0.95rem',
                ...gradientTextSx,
                letterSpacing: '0.15em',
              }}
            >
              {brandName}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        <Typography
          variant="h3"
          sx={{
            fontFamily: "'Syne', sans-serif",
            fontWeight: 700,
            fontSize: { xs: '1.8rem', md: '2.4rem' },
            color: 'var(--t1)',
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ color: 'var(--t3)', fontSize: '0.85rem', mb: 5 }}>
          Last updated: {lastUpdated}
        </Typography>

        <Box
          sx={{
            '& h2': {
              fontFamily: "'Syne', sans-serif",
              fontWeight: 600,
              fontSize: '1.3rem',
              color: 'var(--t1)',
              mt: 5,
              mb: 2,
            },
            '& h3': {
              fontFamily: "'Syne', sans-serif",
              fontWeight: 600,
              fontSize: '1.1rem',
              color: 'var(--t1)',
              mt: 3,
              mb: 1.5,
            },
            '& p': {
              color: 'var(--t2)',
              fontSize: '0.95rem',
              lineHeight: 1.75,
              mb: 2,
            },
            '& ul, & ol': {
              color: 'var(--t2)',
              fontSize: '0.95rem',
              lineHeight: 1.75,
              pl: 3,
              mb: 2,
            },
            '& li': { mb: 0.5 },
            '& a': {
              color: 'var(--teal)',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            },
          }}
        >
          {children}
        </Box>

        <Box sx={{ mt: 8, pt: 4, borderTop: '1px solid var(--border)' }}>
          <Typography sx={{ color: 'var(--t3)', fontSize: '0.85rem' }}>
            Questions about this policy? Contact us at{' '}
            <a href={`mailto:${contactEmail}`} style={{ color: 'var(--teal)' }}>
              {contactEmail}
            </a>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LegalPageLayout;
